import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useClientQueries } from '@zenstackhq/tanstack-query/react';
import { useQueryClient } from '@tanstack/react-query';
import { pickLang } from '@zsp/shared';
import { schema } from '../__generated__/schema-lite';
import { translateError } from '../lib/translate-error';
import { specialistName as specialistNameOf } from '../lib/specialist-name';
import {
  CalendarCheck,
  Clock,
  Phone,
  User,
  FileText,
  Stethoscope,
  MessageSquareText,
  AlertTriangle,
  StickyNote,
  CreditCard,
  ShieldCheck,
  Video,
  Cake,
} from 'lucide-react';
import { CallRecording } from './CallRecording';
import { CallTranscript } from './CallTranscript';

/** The booking conversation linked to an appointment (subset of Call). */
export type AppointmentCall = {
  conversationId: string | null;
  summary: string | null;
  // Transcript now lives in a 1:1 relation (fetched via include). `messages` is
  // the raw provider turns array (present at runtime via `include: transcript`),
  // used to render speaker-differentiated bubbles like the superdoc/admin view.
  transcript?: { text: string | null; messages?: unknown } | null;
} | null;

export type AppointmentRow = {
  id: string;
  patientName: string;
  patientPhone: string;
  reason: string | null;
  startsAt: string | Date;
  endsAt: string | Date;
  status: string;
  source: string;
  rescheduled?: boolean;
  calendarDesynced?: boolean;
  // Richer detail from a Superdoc (source "healee") booking, in a 1:1 side row;
  // null/absent otherwise.
  healee?: {
    patientAge?: number | null;
    patientNote?: string | null;
    isVideo?: boolean;
    bookedOnline?: boolean | null;
    paymentMethodName?: string | null;
    insurerName?: string | null;
    insurerStatus?: string | null;
  } | null;
  createdAt: string | Date;
  specialist?: {
    title: unknown;
    user?: { name: string | null; email: string } | null;
  } | null;
  call?: AppointmentCall;
};

/** Coloured BOOKED / CANCELLED pill. Shared by the list and the detail panel. */
export function AppointmentStatusBadge({
  status,
  rescheduled,
}: {
  status: string;
  rescheduled?: boolean;
}) {
  const { t } = useTranslation();
  const cancelled = status === 'CANCELLED';
  // A rescheduled appointment stays BOOKED (only its time moved) — surface it as
  // its own "Rescheduled" state, distinct from a fresh booking.
  const moved = !cancelled && !!rescheduled;
  const cls = cancelled
    ? 'bg-destructive/10 text-destructive'
    : moved
      ? 'bg-amber-500/10 text-amber-600'
      : 'bg-primary/10 text-primary';
  return (
    <span
      className={'inline-flex w-fit items-center rounded-full px-2 py-0.5 text-[10px] font-medium ' + cls}
    >
      {moved
        ? t('dashboard.appointments.statuses.RESCHEDULED')
        : t(`dashboard.appointments.statuses.${status}`, status)}
    </span>
  );
}

/** Full detail for one appointment — opened in the right split panel on row
 *  click (the calls-dashboard pattern). `showSpecialist` adds the specialist
 *  row for the admin view. */
export function AppointmentDetailPanel({
  appointment: a,
  showSpecialist = false,
  onCancelled,
}: {
  appointment: AppointmentRow;
  showSpecialist?: boolean;
  /** Called after a successful cancel so the opener can close the split panel. */
  onCancelled?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const crud = useClientQueries(schema);
  const queryClient = useQueryClient();
  const { data: meRaw } = crud.$procs.me.useQuery();
  const meRole = (meRaw as { role?: string } | undefined)?.role;
  const cancelMut = crud.$procs.cancelAppointment.useMutation();
  // Staff can cancel a still-booked appointment. ADMIN/SUPERDOC any; a SPECIALIST
  // only their own (enforced server-side — they only ever see their own here).
  const canCancel =
    a.status === 'BOOKED' &&
    (meRole === 'ADMIN' || meRole === 'SUPERDOC' || meRole === 'SPECIALIST');

  async function doCancel() {
    if (!window.confirm(t('appointments.cancelConfirm'))) return;
    try {
      await cancelMut.mutateAsync({ args: { input: { appointmentId: a.id } } });
      // The procedure doesn't auto-invalidate model queries — refresh any
      // Appointment query so the list/calendar reflect the cancellation.
      await queryClient.invalidateQueries({
        predicate: (q) => JSON.stringify(q.queryKey).toLowerCase().includes('appointment'),
      });
      toast.success(t('appointments.cancelled'));
      onCancelled?.();
    } catch (err) {
      toast.error(translateError(t, err));
    }
  }

  const dayFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }),
    [i18n.language],
  );
  const timeFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit' }),
    [i18n.language],
  );
  const createdFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      }),
    [i18n.language],
  );

  const start = new Date(a.startsAt);
  const end = new Date(a.endsAt);
  const specialistName = specialistNameOf(a.specialist as never, i18n.language);

  return (
    <div className="flex h-full flex-col">
      <header className="shrink-0 border-b border-border px-4 py-3 flex items-center gap-2">
        <CalendarCheck className="h-4 w-4 shrink-0 text-primary" />
        <div className="min-w-0">
          <div className="text-sm font-medium text-foreground truncate">{a.patientName}</div>
          <div className="text-xs text-muted-foreground">
            {t('dashboard.appointments.detailSubtitle')}
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-auto p-4 space-y-4">
        {a.calendarDesynced ? (
          <div className="flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 p-2.5 text-xs text-amber-700 dark:text-amber-400">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{t('dashboard.appointments.calendarDesync')}</span>
          </div>
        ) : null}

        <div className="flex items-center gap-2">
          <AppointmentStatusBadge status={a.status} rescheduled={a.rescheduled} />
          <span className="text-[10px] text-muted-foreground">
            {t(`dashboard.appointments.sources.${a.source}`, a.source)}
          </span>
          {a.healee?.isVideo ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/10 px-2 py-0.5 text-[10px] font-medium text-blue-600 dark:text-blue-400">
              <Video className="h-3 w-3" />
              {t('dashboard.appointments.video')}
            </span>
          ) : null}
        </div>

        <dl className="space-y-3 text-sm">
          <Row icon={<Clock className="h-4 w-4" />} label={t('dashboard.appointments.when')}>
            <div>{dayFmt.format(start)}</div>
            <div className="text-muted-foreground tabular-nums">
              {timeFmt.format(start)} – {timeFmt.format(end)}
            </div>
          </Row>

          {showSpecialist ? (
            <Row
              icon={<Stethoscope className="h-4 w-4" />}
              label={t('dashboard.appointments.specialist')}
            >
              <div>{specialistName}</div>
              {pickLang(a.specialist?.title, i18n.language) ? (
                <div className="text-muted-foreground">
                  {pickLang(a.specialist?.title, i18n.language)}
                </div>
              ) : null}
            </Row>
          ) : null}

          <Row icon={<User className="h-4 w-4" />} label={t('dashboard.appointments.patient')}>
            {a.patientName}
          </Row>

          <Row icon={<Phone className="h-4 w-4" />} label={t('dashboard.appointments.phone')}>
            <a href={`tel:${a.patientPhone}`} className="text-primary hover:underline">
              {a.patientPhone}
            </a>
          </Row>

          <Row icon={<FileText className="h-4 w-4" />} label={t('dashboard.appointments.reason')}>
            {a.reason || <span className="text-muted-foreground">—</span>}
          </Row>

          {a.healee?.patientNote ? (
            <Row
              icon={<StickyNote className="h-4 w-4" />}
              label={t('dashboard.appointments.patientNote')}
            >
              {a.healee.patientNote}
            </Row>
          ) : null}

          {a.healee?.patientAge != null ? (
            <Row icon={<Cake className="h-4 w-4" />} label={t('dashboard.appointments.age')}>
              {t('dashboard.appointments.ageYears', { count: a.healee.patientAge })}
            </Row>
          ) : null}

          {a.healee?.paymentMethodName ? (
            <Row
              icon={<CreditCard className="h-4 w-4" />}
              label={t('dashboard.appointments.payment')}
            >
              {a.healee.paymentMethodName}
            </Row>
          ) : null}

          {a.healee?.insurerName || a.healee?.insurerStatus ? (
            <Row
              icon={<ShieldCheck className="h-4 w-4" />}
              label={t('dashboard.appointments.insurer')}
            >
              <span className="inline-flex flex-wrap items-center gap-1.5">
                {a.healee?.insurerName ? <span>{a.healee.insurerName}</span> : null}
                {a.healee?.insurerStatus ? (
                  <span
                    className={
                      'inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ' +
                      (a.healee.insurerStatus === 'approved'
                        ? 'bg-primary/10 text-primary'
                        : a.healee.insurerStatus === 'declined'
                          ? 'bg-destructive/10 text-destructive'
                          : 'bg-amber-500/10 text-amber-600')
                    }
                  >
                    {t(`dashboard.appointments.insurerStatuses.${a.healee.insurerStatus}`, a.healee.insurerStatus)}
                  </span>
                ) : null}
              </span>
            </Row>
          ) : null}
        </dl>

        {/* Linked booking conversation — recording + transcript inline, so the
         *  call no longer needs a separate list to live in. */}
        {a.call && (a.call.conversationId || a.call.summary || a.call.transcript?.text) ? (
          <section className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
            <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              <MessageSquareText className="h-3.5 w-3.5" />
              <span>{t('dashboard.appointments.call')}</span>
            </div>
            <CallRecording conversationId={a.call.conversationId} />
            {a.call.summary ? <p className="text-sm leading-relaxed">{a.call.summary}</p> : null}
            {a.call.transcript?.text ? (
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  {t('calls.transcript')}
                </summary>
                <div className="mt-2 rounded-md border border-border bg-card p-3">
                  <CallTranscript
                    messages={a.call.transcript.messages}
                    transcript={a.call.transcript.text ?? null}
                    callerName={a.patientName}
                  />
                </div>
              </details>
            ) : null}
          </section>
        ) : null}

        <div className="pt-2 border-t border-border text-[11px] text-muted-foreground">
          {t('dashboard.appointments.createdAt', { when: createdFmt.format(new Date(a.createdAt)) })}
        </div>
      </div>
      {canCancel ? (
        <div className="shrink-0 border-t border-border p-3">
          <button
            type="button"
            onClick={() => void doCancel()}
            disabled={cancelMut.isPending}
            className="w-full rounded-lg border border-destructive/40 px-3 py-2 text-sm font-medium text-destructive hover:bg-destructive/10 disabled:opacity-50"
          >
            {t('appointments.cancelAppointment')}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function Row({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex gap-3">
      <span className="mt-0.5 shrink-0 text-muted-foreground">{icon}</span>
      <div className="min-w-0">
        <dt className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </dt>
        <dd className="mt-0.5 wrap-break-word">{children}</dd>
      </div>
    </div>
  );
}
